import React, { useState, useEffect } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '../components/ui/drawer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { XIcon, CheckIcon, PlusCircle } from '@phosphor-icons/react';

import {
  createNotebook,
  getNotebooks,
  getQuestionBookmarkStatus,
  addQuestionToNotebooks,
} from './QuestionBookmark/services/notebooksCreate';
import axios from 'axios';
import toast from 'react-hot-toast';

const BookmarkDrawer = ({
  visible,
  onClose,
  questionId,
  packId,
  testId,
  cookies,
  handleNoteExists,
  handleQuestionNotes = () => {},
  handleQuestionBoomark = () => {},
}) => {
  const [note, setNote] = useState('');
  const [notebooks, setNotebooks] = useState([]);
  const [selectedNotebooks, setSelectedNotebooks] = useState([]);
  const [initialSelectedNotebooks, setInitialSelectedNotebooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingNotebook, setCreatingNotebook] = useState(false);
  const [newNotebookName, setNewNotebookName] = useState('');
  const [sortOrder, setSortOrder] = useState('a-z');
  const [noteLoading, setNoteLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [initialNote, setInitialNote] = useState('');

  const user = JSON.parse(localStorage.getItem('userProfile'));

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setIsClosing(true);
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  useEffect(() => {
    if (visible && questionId) {
      setIsRendered(true);
      setIsClosing(false);
      setTimeout(() => setIsAnimating(true), 10);
      fetchNotebooks();
      fetchBookmarkStatus();
      fetchNotes();
    }
  }, [visible, questionId]);

  const handleAnimationEnd = () => {
    if (isClosing) {
      setIsRendered(false);
    }
  };

  const fetchNotebooks = async () => {
    try {
      setLoading(true);
      const response = await getNotebooks(packId);
      setNotebooks(response.data || []);
    } catch (error) {
      console.error('Error fetching notebooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarkStatus = async () => {
    try {
      const response = await getQuestionBookmarkStatus(questionId);
      if (response.data?.status !== 'Not Added') {
        const notebookIds = response.data?.notebooks?.map((n) => n._id) || [];
        setSelectedNotebooks(notebookIds);
        setInitialSelectedNotebooks(notebookIds);
      } else {
        setSelectedNotebooks([]);
        setInitialSelectedNotebooks([]);
      }
    } catch (error) {
      console.error('Error fetching bookmark status:', error);
    }
  };

  const fetchNotes = () => {
    if (!questionId || !cookies?.token) return;
    setNoteLoading(true);
    axios
      .get(`/api/v2/notes/${questionId}`, {
        headers: { Authorization: `Bearer ${cookies.token}` },
      })
      .then((res) => {
        const noteData = res.data?.data || '';
        setNote(noteData);
        setInitialNote(noteData);
      })
      .catch(() => {
        setNote('');
        setInitialNote('');
      })
      .finally(() => setNoteLoading(false));
  };

  const updateNotes = (noteText) => {
    if (!cookies?.token) return;
    setNoteLoading(true);
    axios
      .patch(
        `/api/v2/notes/${questionId}`,
        { notes: noteText },
        { headers: { Authorization: `Bearer ${cookies.token}` } },
      )
      .then((res) => {
        const updatedNote = res.data?.note || noteText;
        setNote(updatedNote);
        handleQuestionNotes(updatedNote);
        handleNoteExists && handleNoteExists(!!updatedNote);
      })
      .finally(() => setNoteLoading(false));
  };

  const handleNotebookToggle = (notebookId) => {
    setSelectedNotebooks((prev) =>
      prev.includes(notebookId) ? prev.filter((id) => id !== notebookId) : [...prev, notebookId],
    );
  };

  const handleCreateNotebook = async (e) => {
    if (e) e.stopPropagation();
    if (!newNotebookName.trim()) return;
    try {
      setCreatingNotebook(true);
      const response = await createNotebook(newNotebookName, packId);
      window.webengage.track('Notebook Created', {
        'User Id': user?._id,
        'Notebook Name': newNotebookName,
        'Notebook Id': response.notebook._id,
      });
      setNewNotebookName('');
      setShowCreateForm(false);

      await fetchNotebooks();

      if (response.notebook?._id) {
        const newNotebookId = response.notebook._id;
        setSelectedNotebooks((prev) => [...prev, newNotebookId]);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (error) {
    } finally {
      setCreatingNotebook(false);
    }
  };

  const handleSave = async (e) => {
    if (e) e.stopPropagation();
    try {
      setLoading(true);

      // Calculate which notebooks were removed
      const removedNotebookIds = initialSelectedNotebooks.filter((id) => !selectedNotebooks.includes(id));

      // Handle notebook changes (both adding and removing)
      const response = await addQuestionToNotebooks(questionId, selectedNotebooks, removedNotebookIds, packId, testId);

      // Always handle notes changes, regardless of notebook selection
      if (note !== initialNote) {
        updateNotes(note);
      }

      // Update the initial state to reflect current state
      setInitialSelectedNotebooks([...selectedNotebooks]);
      setInitialNote(note);

      // Notify parent components about the changes
      handleQuestionBoomark(selectedNotebooks.length > 0 ? selectedNotebooks : []);

      // Show success message
      if (response.success) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);

        // Close drawer after successful save
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Error saving bookmarks:', error);
      toast.error('Failed to update bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const sortedNotebooks = [...notebooks].sort((a, b) => {
    return sortOrder === 'a-z' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
  });

  const hasChanges = () => {
    const notebookChanges =
      selectedNotebooks.length !== initialSelectedNotebooks.length ||
      !selectedNotebooks.every((id) => initialSelectedNotebooks.includes(id));
    const noteChanged = note !== initialNote;
    return notebookChanges || noteChanged;
  };

  const canSave = hasChanges();

  if (!isRendered && !isClosing) return null;

  return (
    <div className="fixed inset-0 z-100 flex justify-end overflow-hidden">
      <div
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ease-in-out ${
          isAnimating && !isClosing ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ cursor: 'default' }}
        onClick={handleClose}
      />

      <div
        className={`relative flex flex-col h-full bg-[var(--q3-surface-default)] shadow-xl w-full md:w-[600px] transition-all duration-300 ease-in-out ${
          isMobile
            ? `fixed bottom-0 left-0 right-0 transform ${
                isAnimating && !isClosing ? 'translate-y-0' : 'translate-y-full'
              }`
            : `transform ${isAnimating && !isClosing ? 'translate-x-0' : 'translate-x-full'}`
        }`}
        onTransitionEnd={handleAnimationEnd}
      >
        <Button
          onClick={handleClose}
          size="icon"
          className="hidden md:inline-flex rounded-full absolute top-5 -left-10 w-8 h-8 bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dim)]"
        >
          <XIcon size={24} className="text-[var(--q3-neutral-default)]" />
        </Button>

        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-[var(--q3-neutral-default)]">Add Note to Your Qs (Optional)</span>
          </div>

          <Button
            onClick={handleClose}
            size="icon"
            className="inline-flex md:hidden rounded-full w-8 h-8 bg-[var(--q3-surface-default)] hover:bg-[var(--q3-surface-dim)] items-center justify-center"
          >
            <XIcon size={24} className="text-[var(--q3-neutral-default)]" />
          </Button>
        </div>

        <div className="flex-grow p-6 overflow-y-auto">
          <div className="mb-4">
            <Textarea
              placeholder="Type your note here"
              className="hover:border-accent focus:border-accent mb-2 min-h-[100px]"
              style={{
                borderColor: 'var(--q3-stroke-light)',
                backgroundColor: 'var(--q3-surface-default)',
                color: 'var(--q3-neutral-default)',
              }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-medium mb-2" style={{ color: 'var(--q3-neutral-default)' }}>
              Add Qs to Notebook
            </h3>

            {notebooks.length === 0 ? (
              <Card style={{ backgroundColor: 'var(--q3-surface-default)' }}>
                <CardContent className="p-6 text-center">
                  <div className="text-base font-medium mb-2" style={{ color: 'var(--q3-neutral-default)' }}>
                    😕 No Notebook Created!
                  </div>
                  <p className="text-sm" style={{ color: 'var(--q3-neutral-default)' }}>
                    Start by creating your first notebook
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Label className="text-xs font-medium mb-2 mt-4 block" style={{ color: 'var(--q3-neutral-default)' }}>
                  Sort by
                </Label>

                <div className="flex items-center gap-2 mb-4">
                  <Button
                    variant={sortOrder === 'a-z' ? 'default' : 'outline'}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSortOrder('a-z');
                    }}
                    className="rounded-full gap-1"
                    style={{
                      backgroundColor: sortOrder === 'a-z' ? 'var(--q3-accent-normal)' : 'transparent',
                      borderColor: 'var(--q3-stroke-light)',
                      color: sortOrder === 'a-z' ? 'white' : 'var(--q3-neutral-default)',
                    }}
                  >
                    Name (A-Z)
                    {sortOrder === 'a-z' && <CheckIcon className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant={sortOrder === 'z-a' ? 'default' : 'outline'}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSortOrder('z-a');
                    }}
                    className="rounded-full gap-1"
                    style={{
                      backgroundColor: sortOrder === 'z-a' ? 'var(--q3-accent-normal)' : 'transparent',
                      borderColor: 'var(--q3-stroke-light)',
                      color: sortOrder === 'z-a' ? 'white' : 'var(--q3-neutral-default)',
                    }}
                  >
                    Name (Z-A)
                    {sortOrder === 'z-a' && <CheckIcon className="h-4 w-4" />}
                  </Button>
                </div>

                <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh )' }}>
                  {sortedNotebooks.map((notebook) => {
                    const isChecked = selectedNotebooks.includes(notebook._id);
                    return (
                      <div
                        key={notebook._id}
                        className="flex items-center py-2 px-3 rounded-md border cursor-pointer"
                        style={{
                          borderColor: 'var(--q3-stroke-light)',
                          backgroundColor: 'var(--q3-surface-default)',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNotebookToggle(notebook._id);
                        }}
                      >
                        <Checkbox
                          id={`notebook-${notebook._id}`}
                          checked={isChecked}
                          onCheckedChange={() => handleNotebookToggle(notebook._id)}
                          className="mr-3 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Label
                          htmlFor={`notebook-${notebook._id}`}
                          className="text-sm cursor-pointer"
                          style={{ color: 'var(--q3-neutral-default)' }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {notebook.title}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        <div
          className="border-t p-4 relative"
          style={{
            borderTop: '1px solid var(--q3-stroke-normal)',
            background: 'var(--q3-surface-default)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0px -4px 16px 0px rgba(18, 38, 63, 0.16)',
            zIndex: 10,
          }}
        >
          {showSuccess && (
            <div className="absolute bottom-full left-0 right-0 mb-2 flex justify-center">
              <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] rounded-md px-4 py-2 shadow-md">
                <p className="text-sm" style={{ color: 'var(--q3-neutral-default)' }}>
                  Notebooks updated successfully
                </p>
              </div>
            </div>
          )}

          {showCreateForm ? (
            <>
              <h4 className="text-base font-semibold mb-2" style={{ color: 'var(--q3-neutral-default)' }}>
                Create New Notebook
              </h4>

              <Input
                placeholder="Enter notebook name"
                value={newNotebookName}
                onChange={(e) => setNewNotebookName(e.target.value)}
                className="mb-3"
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') handleCreateNotebook(e);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'var(--q3-surface-default)',
                  borderColor: 'var(--q3-stroke-light)',
                  color: 'var(--q3-neutral-default)',
                }}
              />
              <div className="flex items-center w-full gap-3">
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateForm(false);
                    setNewNotebookName('');
                  }}
                  className="flex-1 gap-1 rounded-full"
                  style={{
                    borderColor: 'var(--q3-stroke-light)',
                    color: 'var(--q3-accent-normal)',
                    backgroundColor: 'var(--q3-surface-default)',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateNotebook}
                  disabled={!newNotebookName.trim() || creatingNotebook}
                  className="flex-1 gap-1 rounded-full"
                  style={{
                    backgroundColor: 'var(--q3-accent-normal)',
                  }}
                >
                  Create Notebook
                </Button>
              </div>
            </>
          ) : (
            <div className="flex items-center w-full gap-3">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCreateForm(true);
                }}
                className="flex-1 gap-1 rounded-full"
                style={{
                  borderColor: 'var(--q3-stroke-light)',
                  color: 'var(--q3-accent-normal)',
                  backgroundColor: 'var(--q3-surface-default)',
                }}
              >
                <PlusCircle className="h-4 w-4" />
                Create New Notebook
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave || loading}
                className="flex-1 gap-1 rounded-full"
                style={{
                  backgroundColor: 'var(--q3-accent-normal)',
                }}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookmarkDrawer;
